import * as React from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useMutation, useQuery } from '@apollo/client';
import { Clear, Input, InputErrMess, InputWrap, Label, Submit, UnRegister } from './styles';
import { FETCH_ALL_PAPERS, FETCH_STUDENT_DATA, REGISTER_STUDENT, UNREGISTER_STUDENT } from './graphql';
import {
  FetchStudentDataMutation,
  GetAllPapersQuery,
  RegisterStudentMutation,
  UnRegisterStudentMutation
} from '../../../../../generated/graphql';
import { BeatLoader, ClipLoader } from 'react-spinners';
import { DisplayPaper } from './components/DisplayPaper';
import { toast } from 'react-toastify';
import { useDidMountEffect } from '../../../../../helpers';
import { TabContentOverlayLoader } from '../../../../../components';

export interface Paper {
  __typename?: 'Paper' | undefined;
  courseName?: string | null | undefined;
  _id?: string | null | undefined;
  courseCode?: string | null | undefined;
  class?: string | null | undefined;
}

/**
 * StudentForm
 *
 * Admin form for registering a student and their courses.
 * - Fetches all available papers/courses.
 * - Allows searching and selecting courses for the student.
 * - Prepopulates form if student index number exists.
 * - Submits registration via GraphQL mutation.
 */
export const StudentForm: React.FC = (): JSX.Element => {
  // State for selected courses
  const [selectedCourses, setSelectedCourses] = React.useState<Paper[]>([]);
  // Error state for course selection
  const [showSelectedCourseError, setShowSelectedCourseError] = React.useState(false);
  const [selectedCoursesToDelete, setSelectedCoursesToDelete] = React.useState<Paper[]>([]);
  // Used to trigger reset of child components
  const [refecthCount, setRefetchCount] = React.useState(0);
  // State for filtered papers (search results)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [filteredPapers, setFilteredPapers] = React.useState<any>(null);
  // Search input value
  const [searchValue, setSearchValue] = React.useState('');
  // Data for the current student (if fetched)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [myData, setMyData] = React.useState<any[]>([]);
  // Fetch all available papers/courses
  const { data, loading, error } = useQuery<GetAllPapersQuery>(FETCH_ALL_PAPERS);
  // Mutation to register a student
  const [registerStudent] = useMutation<RegisterStudentMutation>(REGISTER_STUDENT);

  // Mutation to unregister a student 
  const [unregisterStudent] = useMutation<UnRegisterStudentMutation>(UNREGISTER_STUDENT);

  // Mutation to fetch student data by index number
  const [fetchStudentData, { loading: studentDataFetchLoading }] =
    useMutation<FetchStudentDataMutation>(FETCH_STUDENT_DATA, {
      onCompleted: (_data) => {
        if (_data.fetchStudentData) {
          toast.info('Found student with this index number!', {
            autoClose: 2000,
          });
          setFieldValue('name', _data.fetchStudentData.name);
          setMyData(_data.fetchStudentData.papersForStudentClass as never);
          setSelectedCourses(_data.fetchStudentData.registeredPapers as never);
        } else {
          toast.warn('No student with this index exist');
        }
      },
    });

  // Formik validation schema and initial values
  const validationSchema = Yup.object().shape({
    name: Yup.string().required("* Student's full name must be entered"),
    indexNumber: Yup.string()
      .required("* Student's index number must be entered")
      .length(7, "* Student's index number must be 7 digits"),
  });
  const initialValues = {
    name: '',
    indexNumber: '',
  };

  const toggleCourseToDelete = (paper: Paper): void => {
    setSelectedCoursesToDelete((prev) => {
      const isAlreadySelected = prev.some((c) => c._id === paper._id);
      
      if (isAlreadySelected) {
        return prev.filter((c) => c._id !== paper._id);  
      } else {
        return [...prev, paper];                        
      }
    });
  };

const handleUnRegister = async () => {
  if (selectedCoursesToDelete.length === 0) {
    toast.error("Please select at least one course to unregister");
    return;
  }

  if (!values.indexNumber) {
    toast.error("Index number is required");
    return;
  }

  const paperIds = selectedCoursesToDelete.map((course) => course._id!);

  console.log('papers', paperIds);
  

  try {
    await unregisterStudent({
      variables: {
          indexNumber: values.indexNumber.trim(),
          papers: paperIds,
      },
    });

    toast.success(
      `${selectedCoursesToDelete.length} course(s) unregistered successfully!`
    );

    setSelectedCoursesToDelete([]);

    setSelectedCourses((prev) => prev.filter(selectedCourse => !paperIds.includes(selectedCourse._id!)));
    
    // if (values.indexNumber.length === 7) {
    //   await fetchStudentData({
    //     variables: { indexNumber: values.indexNumber },
    //   });
    // }
  } catch (err: any) {
    console.error(err);
    toast.error(err?.message || "Failed to unregister courses");
  }
};

  // Formik setup for the student registration form
  const {
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting,
    handleReset,
    values,
    errors,
    setFieldValue,
  } = useFormik({
    validationSchema,
    initialValues,
    onSubmit: async (formValues) => {
      if (selectedCourses.length === 0) {
        setShowSelectedCourseError(true);
        return;
      }

      // Prepare list of selected paper IDs
      const papersWithIDs = selectedCourses.map((selected) => {
        return `${selected._id}`;
      });

      try {
        await registerStudent({
          variables: {
            studentDetails: {
              indexNumber: formValues.indexNumber,
              name: formValues.name,
              papers: papersWithIDs,
              student_class: selectedCourses[0].class && myData[0].class,
            },
          },
        });
        toast.success('Registered student successfully!');
      } catch (err) {
        if (err.message.includes('Failed to fetch')) {
          toast.error(
            `Registering student failed.
               Network error occured, please check your internet connection!`,
          );
        } else
          toast.error(
            err?.message || 'Error Occured in registering student. Student might already exist',
          );
      }
    },
  });

  // add effect to prepopulate the student details in the form
  const indexNumberInputDep = values.indexNumber.length === 7;
  useDidMountEffect(async () => {
    if (values.indexNumber.length === 7) {
      // fetch for the already existing students details with the index number
      try {
        await fetchStudentData({
          variables: { indexNumber: values.indexNumber },
        });
      } catch (err) {
        console.log(err);
        toast.error(err.message);
      }
    }
  }, [indexNumberInputDep]);

  // Effect: update myData when papers are fetched or refetch is triggered
  React.useEffect(() => {
    if (data?.getPapers) setMyData(data.getPapers);
    if (error) toast.error(error.message);
  }, [data, refecthCount, error]);

  // Effect: filter papers based on search input
  React.useEffect(() => {
    const searchResults = data?.getPapers?.filter((paper) => {
      if (
        paper?.courseCode?.toLowerCase().includes(searchValue.toLowerCase()) ||
        paper?.courseName?.toLowerCase().includes(searchValue.toLowerCase()) ||
        paper?.class?.toLowerCase().includes(searchValue.toLowerCase())
      ) {
        return paper;
      } else {
        return null;
      }
    });

    if (!searchResults) setFilteredPapers(null);

    setFilteredPapers(searchResults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  /**
   * Adds or removes a paper from the selected courses.
   * - If already selected, removes it.
   * - If not selected, adds it.
   */
  const addToSelectedCourse = (paper: Paper): void => {
    if (selectedCourses.includes(paper)) {
      // remove from selected courses
      const filtered = selectedCourses.filter((selected) => paper._id !== selected._id);
      setSelectedCourses(filtered);
      return;
    }
    setSelectedCourses([...selectedCourses, paper]);
  };

  return (
    <form
      className={`mt-14 relative ${studentDataFetchLoading && 'pointer-events-none'}`}
      onSubmit={handleSubmit}
    >
      {/* Overlay loader when fetching student data */}
      {studentDataFetchLoading && (
        <TabContentOverlayLoader loadingMess="Checking for existing student's data" />
      )}
      <div className="pr-10 2xl:pr-16 pb-10 grid grid-cols-2 gap-20">
        {/* Index number and name inputs */}
        <InputWrap>
          <Label>Index Number</Label>
          <Input
            value={values.indexNumber}
            isGrird={true}
            onChange={handleChange}
            name="indexNumber"
            disabled={isSubmitting}
            onBlur={handleBlur}
            placeholder="Enter index number of Student"
          />
          {errors.indexNumber && <InputErrMess>{errors.indexNumber}</InputErrMess>}
        </InputWrap>
        <InputWrap>
          <Label>Student Name</Label>
          <Input
            value={values.name}
            isGrird={true}
            onChange={handleChange}
            disabled={isSubmitting}
            name="name"
            onBlur={handleBlur}
            placeholder="Enter full name of Student"
          />
          {errors.name && <InputErrMess>{errors.name}</InputErrMess>}
        </InputWrap>
      </div>
      <div className="flex p-3 bg-slate-50 -mt-4 rounded-md mb-8">
        {/* Left: Select courses */}
        <div className="flex w-1/2 gap-1 flex-col relative">
          <h4 className="text-center font-extrabold my-2">Select student&apos;s courses</h4>
          <div
            className="bg-gray-100 select-none w-11/12
             rounded-md p-5 gap-3 shadow flex flex-col"
          >
            <h3 className="font-extrabold">All Papers</h3>
            {loading ? (
              <div className="flex justify-center items-center">
                <ClipLoader size={40} />
              </div>
            ) : (
              <>
                {/* Search input for filtering papers */}
                <div className="w-full">
                  <input
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    className="py-2 w-full px-3 border-gray-300 bg-white rounded-md
                      outline-none"
                    placeholder="Search for a paper by course code or name"
                  />
                </div>
                {/* List of all or filtered papers */}
                <div
                  className="flex flex-col gap-3 lg:h-90 2xl:h-[27rem]
                 2xl:pb-10 pb-9 pr-2 overflow-auto"
                >
                  {error || filteredPapers?.length === 0 || myData.length === 0 ? (
                    <p className="text-center text-red-600">{'Opps! ):'} No paper was found</p>
                  ) : (
                    <>
                      {!filteredPapers
                        ? myData?.map((paper) => (
                            <DisplayPaper
                              key={paper?._id}
                              paper={paper}
                              addToSelectedCourse={addToSelectedCourse}
                              reset={refecthCount}
                            />
                          ))
                        : // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          filteredPapers?.map((paper: any) => (
                            <DisplayPaper
                              key={paper?._id}
                              paper={paper}
                              addToSelectedCourse={addToSelectedCourse}
                              reset={refecthCount}
                            />
                          ))}
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
        {/* Right: List of selected/registered courses */}
        <div className='w-1/2'>
            <h4 className="text-center font-extrabold my-2 mt-8">
              Courses (Tap to select for unregistration)
              </h4>
          {selectedCourses.length === 0 ? (
            <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg mb-8">
              <p className='text-gray-500'>No courses selected for registration yet!</p>
              {showSelectedCourseError && (
                <p className="text-red-600">* Please select at least one course to register!</p>
              )}
            </div>
          ) : (
            <div className="flex gap-2 overflow-auto lg:h-96 2xl:h-[28rem] pb-6 mb-8 bg-gray-50 p-3 rounded-lg">
              <div className="w-11/12 flex flex-col gap-2">
                {selectedCourses.map((course: Paper) => {
                  const isSelectedForDelete = selectedCoursesToDelete.some(
                    (c) => c._id === course._id
                  );
                return (
                  <div key={course._id}
                  onClick={() => toggleCourseToDelete(course)}
                  className={`p-3 rounded-md cursor-pointer transition-all ${
                    isSelectedForDelete
                      ? 'bg-red-100 border border-red-400'
                      : 'bg-blue-100 border border-blue-200 hover:bg-blue-200'
                  }`}
                  >
                    <b> {course.courseCode}, </b>
                    {course.courseName} - {course.class}
                  
                  </div>
                );
              })}
              </div>
            </div>
          )}
      </div>
    </div>

    {console.log('selected:', selectedCoursesToDelete)}
          
      {/* Submit and reset buttons */}
      <div className="mt-8 flex gap-4">
        <Submit className="font-bold" type="submit">
          {isSubmitting ? <BeatLoader size={14} color="#fff" /> : 'Register'}
        </Submit>
        <UnRegister 
          className="font-bold"
          onClick={handleUnRegister}
          type="button"   
        >
          Unregister
        </UnRegister>
        <Clear
          className="font-bold"
          onClick={(e) => {
            handleReset(e);
            setSelectedCourses([]);
            setRefetchCount(refecthCount + 1);
            setSearchValue('');
          }}
        >
          Reset
        </Clear>
      </div>
    </form>
  );
};
